/**
 * RegionSearch
 *
 * Autocomplete search bar that lets users jump to any country, city, or region.
 * Uses the Mapbox Geocoding API via the useRegionSearch hook.
 */
import { useRef, useState } from 'react';
import { useRegionSearch } from '@/hooks/useRegionSearch';
import { cn } from '@/lib/utils';

export default function RegionSearch() {
  const { query, handleChange, suggestions, isFetching } = useRegionSearch();
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  const handleSelect = (suggestion) => {
    handleChange(suggestion.name);
    setIsOpen(false);
    inputRef.current?.blur();

    // Fire a custom event that MapView.jsx listens to
    window.dispatchEvent(
      new CustomEvent('km:map:flyTo', {
        detail: {
          center: suggestion.center,
          bbox: suggestion.bbox,
          zoom: 6,
        },
      })
    );
  };

  return (
    <div className="relative">
      <div className="flex items-center rounded-xl border border-slate-700 bg-impact-dark/90 px-3 py-2 shadow-lg backdrop-blur-sm focus-within:border-brand-500 transition">
        {/* Search icon */}
        <svg className="mr-2 h-4 w-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="8" />
          <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
        </svg>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { handleChange(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          placeholder="Search region, country, city…"
          className="flex-1 bg-transparent text-sm text-slate-100 placeholder-slate-500 focus:outline-none"
        />

        {isFetching && (
          <div className="ml-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-1 overflow-hidden rounded-xl border border-slate-700 bg-impact-dark/95 shadow-2xl backdrop-blur-sm animate-fade-in z-50">
          {suggestions.map((s) => (
            <li key={s.id}>
              <button
                onMouseDown={() => handleSelect(s)}
                className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm transition hover:bg-slate-800"
              >
                <svg className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={cn('truncate text-slate-200', s.name.length > 40 && 'text-xs')}>
                  {s.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
